import { PosGraduacaoResponse } from '@/core/api/sicom-api/registro/medico/formacao/pos-graduacao.model';

export interface FormacaoGradFormValue {
    tipoFormacao: number | '' | null;
    comprovanteGraduacao: number | null;
    idTipoDiploma?: number | null;
    gradEscolaId: number | null;
    gradEscolaUf: string | null;
    nomeEscola: string;
    nomeAnteriorEscola: string;
    dataCriacaoEscola: string;
    dataCriacaoCurso: string;
    dataInicioCurso?: string;
    dataFormatura: string;
    dataConclusaoCurso?: string;
    expedirDiploma: string;
    cargaHoraria?: string;
    tese?: string;
    numeroProcessoDiploma: string;
    livroRegistroDiploma: string;
    numeroFolhaRegistro: string;
    numeroRegistroMEC: string;
    dataRegistroMEC: string;
    autorizaDivulgacao: boolean;
    apresentouDiploma?: boolean;
    paisOrigemFaculdade: number | null;
    nomeFaculdadeEstrangeira: string;
    urlDocumentosComprobatorios: string;
    idPrestadorIes?: number | null;
    ufPrestadorIes?: string | null;
    nomeCurso?: string;
    idLocalDiploma?: number | null;
    codigoValidacaoDiploma?: string;
    urlValidacaoDiploma?: string;
    nomeXmlDiploma?: string;
    nomeRelatorioDiploma?: string;
    nomeRepresentacaoGraficaDiploma?: string;
}

export interface FormacaoPosFormValue {
    posIesId: number | null;
    posIesUf: string | null;
    posNome: string;
    posDenominacaoCurso: number | '' | null;
    posComprovanteGraduacao: number | null;
    posDtInicioOferta: string;
    posDtInicioCurso: string;
    posDtFimCurso: string;
    posDtFormatura: string;
    posCargaHoraria: string | number | null;
    posUrlDocumento: string;
    posIdDocumento?: number | string | null;
}

export interface PessoaLike {
    id?: number | string | null;
    uf?: string | null;
}

// Utilitário: converte data DD/MM/AAAA para ISO YYYY-MM-DD
export function toIsoDate(value?: string | null): string | undefined {
    if (!value) return undefined;
    const s = String(value).trim();
    if (!s) return undefined;
    const parts = s.split('/');
    if (parts.length !== 3) return undefined;
    const [dd, mm, yyyy] = parts;
    if (!yyyy || !mm || !dd) return undefined;
    const d = parseInt(dd, 10);
    const m = parseInt(mm, 10);
    const y = parseInt(yyyy, 10);
    if (isNaN(d) || isNaN(m) || isNaN(y)) return undefined;
    const ddP = String(d).padStart(2, '0');
    const mmP = String(m).padStart(2, '0');
    return `${y}-${mmP}-${ddP}`;
}

export function buildGraduacaoPayload(pessoa: PessoaLike, v: FormacaoGradFormValue) {
    return {
        registro: '',
        medicoIdSolicitante: pessoa.id,
        medicoUfSgUf: pessoa.uf,
        tipoFormaIdTipoFormacao: v.tipoFormacao ?? null,
        idTipoFormacaoDiploma: v.comprovanteGraduacao ?? null,
        idTipoDiploma: v.idTipoDiploma ?? null,
        dtFormatura: toIsoDate(v.dataFormatura) ?? null,
        dtInicioCurso: toIsoDate(v.dataInicioCurso) ?? null,
        dtConclusaoCurso: toIsoDate(v.dataConclusaoCurso) ?? null,
        txCargaHoraria: v.cargaHoraria ?? '',
        txTese: v.tese ?? '',
        dtExpedicaoDiploma: toIsoDate(v.expedirDiploma) ?? null,
        nuLivroDiploma: v.livroRegistroDiploma ?? '',
        nuProcessoDiploma: v.numeroProcessoDiploma ?? '',
        nuFolhaDiploma: v.numeroFolhaRegistro ?? '',
        nuRegistroMec: v.numeroRegistroMEC ?? '',
        dtRegistroMec: toIsoDate(v.dataRegistroMEC) ?? null,
        nmAnterior: v.nomeAnteriorEscola ?? '',
        nmEscola: v.nomeEscola ?? '',
        dtCriacao: toIsoDate(v.dataCriacaoEscola) ?? null,
        dtCriacaoCurso: toIsoDate(v.dataCriacaoCurso) ?? null,
        inDivulga: v.autorizaDivulgacao ? 'S' : 'N',
        inApresentouDiploma: v.apresentouDiploma === false || v.comprovanteGraduacao === 3 ? 'N' : 'S',
        escolaIdEscola: v.gradEscolaId ?? null,
        escolaUfSgUf: v.gradEscolaUf ?? null,
        idPais: v.paisOrigemFaculdade ?? null,
        nmFaculdadeEstrangeira: v.nomeFaculdadeEstrangeira ?? '',
        imDocumento: v.urlDocumentosComprobatorios ?? '',
        idPrestadorIes: v.idPrestadorIes ?? null,
        sgUfPrestadorIes: v.ufPrestadorIes ?? null,
        nmCurso: v.nomeCurso ?? '',
        idLocal: v.idLocalDiploma ?? null,
        coValidacaoDiploma: v.codigoValidacaoDiploma ?? '',
        txUrlValidacao: v.urlValidacaoDiploma ?? '',
        nmXml: v.nomeXmlDiploma ?? '',
        nmRelConfirmacao: v.nomeRelatorioDiploma ?? '',
        nmRepGrafica: v.nomeRepresentacaoGraficaDiploma ?? ''
    };
}

export function buildPosPayload(pessoa: PessoaLike, v: FormacaoPosFormValue, nmCursoEspecializacao?: string): PosGraduacaoResponse {
    const qtCargaHoraria = v.posCargaHoraria != null && String(v.posCargaHoraria).trim() !== '' ? Number(String(v.posCargaHoraria).replace(/\D+/g, '')) : undefined;
    const idDocumento = v.posIdDocumento != null && String(v.posIdDocumento).trim() !== '' ? Number(String(v.posIdDocumento).replace(/\D+/g, '')) : undefined;

    return {
        idSolicitante: Number(pessoa.id),
        sgUf: String(pessoa.uf),
        coCursoEspecializacao: (v as { posDenominacaoCurso?: number }).posDenominacaoCurso ?? undefined,
        coIes: v.posIesId != null ? Number(v.posIesId) : undefined,
        nmEscola: v.posNome ?? undefined,
        nmCursoEspecializacao,
        dtInicioOferta: toIsoDate(v.posDtInicioOferta),
        dtInicio: toIsoDate(v.posDtInicioCurso),
        dtFim: toIsoDate(v.posDtFimCurso),
        dtFormatura: toIsoDate(v.posDtFormatura),
        qtCargaHoraria: isNaN(qtCargaHoraria as number) ? undefined : (qtCargaHoraria as number),
        idDocumento: isNaN(idDocumento as number) ? undefined : (idDocumento as number),
        idTipoFormacaoDiploma: v.posComprovanteGraduacao ?? undefined,
        txUrlDocumento: v.posUrlDocumento || undefined
    };
}
